import type { Notification as NovuNotification } from '@novu/js';

import type {
  AppNotification,
  BaseNotification,
  FallbackNotification,
  KnownNotificationWorkflowId,
  NotificationCurrencyCode,
  NotificationTransferType,
} from '../types/notifications.types';

type NotificationBaseFields = Pick<
  BaseNotification,
  'id' | 'createdAt' | 'read' | 'subject' | 'body' | 'tags'
>;

export type MappableNovuNotification = Pick<
  NovuNotification,
  'id' | 'createdAt' | 'isRead' | 'body' | 'subject'
> & {
  tags?: string[];
  data?: unknown;
  workflow?: {
    identifier?: string;
  };
};

const transferTypes: NotificationTransferType[] = ['SEPA', 'SWIFT'];
const currencyCodes: NotificationCurrencyCode[] = ['EUR', 'GBP', 'JPY', 'BHD', 'KWD', 'OMR', 'USD'];

const toRecord = (value: unknown): Record<string, unknown> => {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
};

const toString = (value: unknown): string => (typeof value === 'string' ? value : '');

const toNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const toTransferType = (value: unknown): NotificationTransferType => {
  if (typeof value !== 'string') {
    return 'SEPA';
  }

  return transferTypes.includes(value as NotificationTransferType)
    ? (value as NotificationTransferType)
    : 'SEPA';
};

const toCurrencyCode = (value: unknown): NotificationCurrencyCode => {
  if (typeof value !== 'string') {
    return 'EUR';
  }

  return currencyCodes.includes(value as NotificationCurrencyCode)
    ? (value as NotificationCurrencyCode)
    : 'EUR';
};

// ── Mapper type ───────────────────────────────────────────────────────
/**
 * A mapper receives the common base fields and the raw Novu `data` bag,
 * and returns a fully-typed `AppNotification` variant.
 *
 * To add a new workflow:
 *  1. Define its type in `types/notifications.types.ts`
 *  2. Add it to the `AppNotification` union
 *  3. Register a mapper here with `registerMapper()`
 */
type WorkflowMapper = (
  base: NotificationBaseFields,
  data: Record<string, unknown>,
) => AppNotification;

// ── Registry ──────────────────────────────────────────────────────────
const registry = new Map<KnownNotificationWorkflowId, WorkflowMapper>();

const registerMapper = (workflowId: KnownNotificationWorkflowId, mapper: WorkflowMapper) => {
  registry.set(workflowId, mapper);
};

// ── Per-workflow mappers ──────────────────────────────────────────────

registerMapper('kyc-completed-success', (base, data) => ({
  ...base,
  workflowId: 'kyc-completed-success',
  renderGroup: 'status',
  payload: {
    customExtraText: toString(data.customExtraText),
  },
}));

registerMapper('welcome-to-the-app-emaill-verified', (base, data) => ({
  ...base,
  workflowId: 'welcome-to-the-app-emaill-verified',
  renderGroup: 'status',
  payload: data,
}));

const statusPayloadPassthroughWorkflows = [
  'kyc-verification-completed',
  'kyc-verification-in-review',
  'kyc-verification-retry',
  'kyb-verification-completed',
  'kyb-verification-in-review',
  'kyb-verification-retry',
] as const;

statusPayloadPassthroughWorkflows.forEach((workflowId) => {
  registerMapper(workflowId, (base, data) => ({
    ...base,
    workflowId,
    renderGroup: 'status',
    payload: data,
  }));
});

const transactionAccountStatusWorkflows = [
  'transaction-account-activated',
  'transaction-account-inactivated',
] as const;

transactionAccountStatusWorkflows.forEach((workflowId) => {
  registerMapper(workflowId, (base, data) => ({
    ...base,
    workflowId,
    renderGroup: 'status',
    payload: {
      iban: toString(data.iban),
      accountCurrencies: toString(data.accountCurrencies),
    },
  }));
});

registerMapper('incoming-transfer', (base, data) => ({
  ...base,
  workflowId: 'incoming-transfer',
  renderGroup: 'transfer',
  payload: {
    transferSourceName: toString(data.transferSourceName),
    transferSourceAmount: toString(data.transferSourceAmount),
    transferType: toTransferType(data.transferType),
    transferId: toString(data.transferId),
  },
}));

registerMapper('incoming-transfer-completed', (base, data) => ({
  ...base,
  workflowId: 'incoming-transfer-completed',
  renderGroup: 'transfer',
  payload: {
    amount: toNumber(data.amount),
    currency: toCurrencyCode(data.currency),
    senderName: toString(data.senderName),
  },
}));

const outgoingTransferWorkflows = [
  'outgoing-transfer-completed',
  'outgoing-transfer-cancelled',
  'outgoing-transfer-failed',
  'outgoing-transfer-returned',
] as const;

outgoingTransferWorkflows.forEach((workflowId) => {
  registerMapper(workflowId, (base, data) => ({
    ...base,
    workflowId,
    renderGroup: 'transfer',
    payload: {
      amount: toNumber(data.amount),
      currency: toCurrencyCode(data.currency),
      recipientFullName: toString(data.recipientFullName),
    },
  }));
});

const exchangeTransferWorkflows = [
  'exchange-transfer-completed',
  'exchange-transfer-failed',
  'exchange-transfer-cancelled',
] as const;

exchangeTransferWorkflows.forEach((workflowId) => {
  registerMapper(workflowId, (base, data) => ({
    ...base,
    workflowId,
    renderGroup: 'exchange',
    payload: {
      fromAmount: toNumber(data.fromAmount),
      fromCurrency: toCurrencyCode(data.fromCurrency),
      toAmount: toNumber(data.toAmount),
      toCurrency: toCurrencyCode(data.toCurrency),
    },
  }));
});

const toFallbackNotification = (
  base: NotificationBaseFields,
  sourceWorkflowId: string,
  payload: Record<string, unknown>,
): FallbackNotification => ({
  ...base,
  workflowId: 'unknown-workflow',
  sourceWorkflowId,
  renderGroup: 'fallback',
  payload,
});

// ── Public API ────────────────────────────────────────────────────────

/**
 * Converts a Novu `Notification` into the app's `AppNotification` discriminated union.
 * Returns fallback notification for unrecognised workflow types (logged as warning in dev).
 */
export const toAppNotification = (n: MappableNovuNotification): AppNotification | null => {
  const workflowId = n.workflow?.identifier;
  if (!workflowId) return null;

  const payload = toRecord(n.data);

  const base: NotificationBaseFields = {
    id: n.id,
    createdAt: n.createdAt,
    read: n.isRead,
    subject: n.subject,
    body: n.body,
    tags: n.tags ? [...n.tags] : [],
  };

  const mapper = registry.get(workflowId as KnownNotificationWorkflowId);
  if (!mapper) {
    if (import.meta.env.DEV) {
      console.warn(`[notifications.mappers] Unregistered workflow: "${workflowId}"`);
    }
    return toFallbackNotification(base, workflowId, payload);
  }

  return mapper(base, payload);
};

/**
 * Batch-maps a list of Novu notifications, filtering out entries without workflow identifier.
 */
export const mapNotifications = (notifications: MappableNovuNotification[]): AppNotification[] =>
  notifications.map(toAppNotification).filter((n): n is AppNotification => n !== null);
